import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import api from '../../../api/axios';

interface TaskDeleteDialogProps {
  open: boolean;
  task: any | null;
  deleting: boolean;
  caseId: string;
  headers: any;
  availableUsers: any[];
  onClose: () => void;
  onDeleteStart: () => void;
  onDeleteEnd: () => void;
  onSuccess: (tasks: any[]) => void;
  onError: (error: string) => void;
  onRefreshTimeline: () => void;
}

export const TaskDeleteDialog: React.FC<TaskDeleteDialogProps> = ({
  open,
  task,
  deleting,
  caseId,
  headers,
  availableUsers,
  onClose,
  onDeleteStart,
  onDeleteEnd,
  onSuccess,
  onError,
  onRefreshTimeline,
}) => {
  const handleDelete = async () => {
    if (!task) return;

    onDeleteStart();
    try {
      await api.delete(`/tasks/${task.id}/`, { headers });

      // Refresh pending tasks
      const response = await api.get(`/tasks/?case=${caseId}`, { headers });
      const tasks = response.data.results || response.data || [];
      const pendingAndInProgressTasks = tasks.filter((t: any) =>
        t.status === 'pending' || t.status === 'in_progress'
      );
      const enrichedTasks = pendingAndInProgressTasks.map((t: any) => {
        const assignedUser = availableUsers.find(u => u.id === t.assigned_to);
        return {
          ...t,
          assigned_to_name: assignedUser
            ? (assignedUser.first_name && assignedUser.last_name
              ? `${assignedUser.first_name} ${assignedUser.last_name}`
              : assignedUser.email)
            : null
        };
      });

      // Sort tasks by due date (earliest first), then by created_at
      enrichedTasks.sort((a: any, b: any) => {
        if (a.due_date && !b.due_date) return -1;
        if (!a.due_date && b.due_date) return 1;
        if (a.due_date && b.due_date) {
          const dateComparison = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
          if (dateComparison !== 0) return dateComparison;
        }
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });

      onSuccess(enrichedTasks);
      onRefreshTimeline();
      onClose();
    } catch (err) {
      console.error('Failed to delete task:', err);
      onError('Failed to delete task');
    } finally {
      onDeleteEnd();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !deleting && onClose()}
      maxWidth="sm"
    >
      <DialogTitle>Delete Task</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete the task "{task?.title}"? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={deleting}
        >
          {deleting ? <CircularProgress size={20} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
